// @flow
import React, { type Node, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import DayPicker, { DateUtils } from 'react-day-picker';
import type { Modifier, Modifiers, ClassNames } from 'react-day-picker';

import * as MomentLocaleUtils from './Datepicker.utils';
import { DatepickerArrow } from './DatepickerArrow.jsx';
import { DatepickerCaption } from './DatepickerCaption.jsx';
import { themes } from './Datepicker.themes';

export type DatepickerProps = {
  className?: string,
  customModifiers?: Modifiers,
  disabledDays?: Modifier[],
  datePickerClassNames?: ClassNames,
  hasNavigationByYear?: boolean,
  hasRangeMode?: boolean,
  initialMonth?: Date,
  locale?: string,
  minDate: Date,
  maxDate: Date,
  onMouseOver?: (day: ?Date) => mixed,
  onSelect?: ({
    from: ?Date,
    to: ?Date,
    modifiers: Modifiers,
  }) => mixed,
  renderDay?: (day: Date, modifiers: Modifiers) => Node,
  selectedDays?: Modifier,
  theme?: string,
};

function isSelectingFirstDay(from: ?Date, to: ?Date, day: Date): boolean {
  const isBeforeFirstDay = Boolean(from && DateUtils.isDayBefore(day, from));
  const isRangeSelected = Boolean(from && to);
  return !from || isBeforeFirstDay || isRangeSelected;
}

function isChangingSelection(from: ?Date, to: ?Date, day: Date): boolean {
  return Boolean(from && to && day >= from && day <= to);
}

function isSelectingSameDay(from: ?Date, to: ?Date, day: Date): boolean {
  return Boolean(from && !to && DateUtils.isSameDay(from, day));
}

export const Datepicker = (props: DatepickerProps) => {
  const {
    className,
    maxDate,
    minDate,
    hasNavigationByYear,
    initialMonth,
    locale = moment.locale(),
    renderDay,
    theme = 'default',
    customModifiers = {},
    datePickerClassNames = {},
    disabledDays = [],
    selectedDays,
    hasRangeMode,
    onSelect,
    onMouseOver,
  } = props;

  const { from, to } =
    !selectedDays || DateUtils.isDate(selectedDays)
      ? { from: selectedDays, to: selectedDays }
      : selectedDays;
  const month = from || initialMonth || minDate || new Date();

  const [hovered, setHovered] = useState<Date | null>(null);
  const [activeMonth, setActiveMonth] = useState<?Date>(month);

  useEffect(
    () => {
      setActiveMonth(month);
    },
    [moment(month, moment.defaultFormat).format('YYYYMMDD')],
  );

  const handleDayClick = (day: Date, modifiers: *) => {
    setHovered(null);

    if (onSelect) {
      if (!hasRangeMode) {
        onSelect({ from: day, to: day, modifiers });
      } else if (isSelectingFirstDay(from, to, day) || isChangingSelection(from, to, day)) {
        onSelect({ from: day, to: null, modifiers });
      } else if (isSelectingSameDay(from, to, day)) {
        onSelect({ from: null, to: null, modifiers });
      } else {
        onSelect({ from, to: day, modifiers });
      }
    }
  };

  const handleDayMouseEnter = (day: Date) => {
    setHovered(day);

    if (onMouseOver) {
      onMouseOver(day);
    }
  };

  const handleDayMouseLeave = () => {
    setHovered(null);
  };

  const addMonthsInRange = (date: Date, number: number) => {
    const targetMonth = moment(date)
      .add({ months: number })
      .toDate();

    if (minDate && DateUtils.isDayBefore(targetMonth, minDate)) return minDate;
    if (maxDate && DateUtils.isDayAfter(targetMonth, maxDate)) return maxDate;
    return targetMonth;
  };

  const handleNextMonth = () => {
    if (!activeMonth) return;

    setActiveMonth(addMonthsInRange(activeMonth, 1));
  };

  const handlePreviousMonth = () => {
    if (!activeMonth) return;

    setActiveMonth(addMonthsInRange(activeMonth, -1));
  };

  const handleNextYear = () => {
    if (!activeMonth) return;

    setActiveMonth(addMonthsInRange(activeMonth, 12));
  };

  const handlePreviousYear = () => {
    if (!activeMonth) return;

    setActiveMonth(addMonthsInRange(activeMonth, -12));
  };

  const allDisabledDays = [
    ...disabledDays,
    {
      before: minDate,
      after: maxDate,
    },
  ];

  const { thDayEnd, thDayStart, thDayMiddle, thDayPicker, thRoot } = themes[theme];

  const allSelectedDays = [from, { from, to: to || hovered }, hovered];

  const modifiers = {
    ...customModifiers,
    [thDayStart]: from,
    [thDayEnd]: to,
    [thDayMiddle]: hovered,
  };

  return (
    <div className={classnames(className, thRoot)}>
      <DayPicker
        canChangeMonth
        firstDayOfWeek={moment.localeData().firstDayOfWeek()}
        fixedWeeks
        locale={locale}
        localeUtils={MomentLocaleUtils}
        showOutsideDays
        showWeekDays
        captionElement={
          <DatepickerCaption
            className={thDayPicker.caption}
            fromMonth={minDate}
            hasNavigationByYear={hasNavigationByYear}
            onNext={handleNextYear}
            onPrevious={handlePreviousYear}
            toMonth={maxDate}
          />
        }
        classNames={{ ...thDayPicker, ...datePickerClassNames }}
        disabledDays={allDisabledDays}
        fromMonth={minDate}
        initialMonth={initialMonth}
        modifiers={modifiers}
        month={activeMonth}
        navbarElement={
          <DatepickerArrow
            onNext={handleNextMonth}
            onPrevious={handlePreviousMonth}
            fromMonth={minDate}
            locale={locale}
            toMonth={maxDate}
          />
        }
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        onDayTouchEnd={handleDayMouseEnter}
        onDayMouseLeave={handleDayMouseLeave}
        renderDay={renderDay}
        selectedDays={allSelectedDays}
        toMonth={maxDate}
      />
    </div>
  );
};
